import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  inject,
  Input,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import {
  FooterBaseComponent,
  Instance,
  InstanceResolveData,
  doArrayDirtyCheck,
  OverlayService,
} from '@finra/rampdi-forms-common';
import { CompletenessCheckService } from '../../services/completeness-check.service';
import { InstanceService, MU3WithSupportingData } from '../../services/instance.service';
import { RedlineService } from '../../services/redline.service';
import { BehaviorSubject, concatMap, EMPTY, filter, map, Observable, Subject, takeUntil, timer } from 'rxjs';
import { SharedStateService } from '../../services/shared-state.service';
import {
  CanAttestResponse,
  DisplayRulesService,
  FilingStatusDisplayRules,
  FILING_STATUS,
} from '../../services/display-rules.service';
import { CollectionInstanceUpdateRequest, ExternalService } from '@finra/angular-data-collection-sdk';
import { environment, Versions } from 'projects/app-mu3-form/src/environments/environment';

@Component({
  selector: 'app-footer',
  templateUrl: './footer.component.html',
  styleUrls: ['./footer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  inputs: ['section', 'lastOpenedSectionDE', 'saveInProgress', 'viewType'],
  outputs: ['submit', 'save', 'validate', 'next', 'back', 'print', 'redline', 'updateOptions'],
})
export class FooterComponent
  extends FooterBaseComponent<
    MU3WithSupportingData,
    Instance<any>,
    InstanceResolveData<MU3WithSupportingData, Instance<any>>
  >
  implements OnInit, OnDestroy
{
  @Input() isWebCRDDown;
  @Input() sectionNavData;
  autoSaveData$;
  displayRules$: Observable<FilingStatusDisplayRules> = inject(DisplayRulesService).displayRulesSubj;
  isDisableSub$ = new BehaviorSubject<boolean>(true);
  isRequestAttestationDisabledSub$ = new BehaviorSubject<boolean>(false);
  successfulRequestAttestation = 'MU3 has been sent to the individual for attestation.';
  successfulAttestation = 'MU3 has been sent to your company for payment and final submission.';
  private destroySub$: Subject<void> = new Subject<void>();
  navSelectedSection: string;
  redlineChecked = false;
  individualChangesChecked = false;
  isInstanceSuccessfullyProcessed = false;
  showIndividualChanges$ = new BehaviorSubject<boolean>(false);

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private OS: OverlayService,
    private CC: CompletenessCheckService,
    private RL: RedlineService,
    private IS: InstanceService,
    private ES: ExternalService,
    private DS: DisplayRulesService,
    private cd: ChangeDetectorRef,
    private sharedStateService: SharedStateService,
  ) {
    super(router, route, OS, CC, RL, IS);
  }

  get isRegulatorUser(): boolean {
    return this.DS.isRegulatorUser.value;
  }

  /**
   * Determines if the "View Individual Changes Only" toggle should be shown
   * Only show for company users when viewing attested forms
   */
  shouldShowIndividualChangesToggle(): boolean {
    const currentRules = this.DS.getCurrentDisplayRules();
    const currentFilingStatus = currentRules?.filingStatus;

    return this.DS.userPermissions.isCompanyUser && currentFilingStatus === FILING_STATUS.ATTESTED;
  }

  ngOnInit() {
    this.updateCompletenessCheckTitle(); // Set initial title
    this.completenessCheckHandler();
    this.DS.canAttestResp$.pipe(takeUntil(this.destroySub$)).subscribe((res) => {
      const shouldShowEmploymentCard =
        this.DS.userPermissions.isIndividualUser &&
        (this.IS.currentInstance?.collectedData?.employmentHistory?.some(
          (emp) => emp.status === 'COMPANYRELATIONSHIP' && emp.isAttested !== true,
        ) ??
          false);
      const employmentCheckPasses = shouldShowEmploymentCard ? res.employmentHistoryReviewed ?? false : true;
      this.isDisableSub$.next(
        !(res.attestChecked && employmentCheckPasses && !res.hasError && !res.isCompletenessCheckLoading),
      );
    });
    this.autoSaveData$ = this.IS.saveMetadata$;

    // Initialize the shared state service with the current instance status
    if (this.IS.currentInstance?.instanceMetadata?.status === 'SUBMITTED') {
      this.sharedStateService.setPostSubmission(true);
      this.isInstanceSuccessfullyProcessed = true;
    }

    this.route.queryParams.subscribe((params) => {
      if (params?.redlineCheck) {
        this.onToggleRedline(true);
        this.router.navigate([], {
          queryParams: { redlineCheck: null },
          queryParamsHandling: 'merge',
          replaceUrl: true,
        });
      }
    });
  }

  completenessCheckHandler() {
    this.navSelectedSection =
      this.sectionNavData?.[0]?.children?.find((section) => section?.active)?.id ?? this.navSelectedSection;
    if (this.navSelectedSection === 'attestAndSubmit') {
      // to make sure whenever handler triggers it will check for cloudwell and remove it if its there
      delete this.IS.currentInstance.collectedData?.cloudwell;
      this.CC.setCompletenessCheckData({ close: true });
    }
    this.CC.completenessCheck$
      .pipe(
        takeUntil(this.destroySub$),
        map((ccResp) => {
          if (ccResp?.fail) {
            this.updateAttestState({
              hasError: true,
              isCompletenessCheckLoading: ccResp?.loading !== undefined ? !!ccResp.loading : true,
            });
            return EMPTY;
          }

          //Open CC only on Attest and Submit page
          if (this.navSelectedSection === 'attestAndSubmit' && ccResp?.close && !this.isInstanceProcessedOrFailed()) {
            // to make sure whenever handler triggers it will check for cloudwell and remove it if its there
            delete this.IS.currentInstance.collectedData?.cloudwell;
            this.onValidate();
            this.CC.setCompletenessCheckData({ open: true, loading: true });
          }

          const errorCount = ccResp?.count?.error ? ccResp?.count?.error : 0;
          //Check ccResp is successful
          const hasError = errorCount > 0 && !ccResp?.success;

          this.updateAttestState({
            hasError,
            isCompletenessCheckLoading:
              ccResp?.open === false ? false : ccResp?.loading !== undefined ? !!ccResp.loading : true,
            errorCount,
          });
        }),
      )
      .subscribe();
  }

  private updateAttestState(state: Partial<CanAttestResponse>) {
    this.DS.canAttestResp.next({
      ...this.DS.canAttestResp.getValue(),
      ...state,
    });
  }

  ngAfterViewInit(): void {
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      let currentRoute = event.urlAfterRedirects;
      let attestAndSubmitRoute = currentRoute.includes('attestAndSubmit');
      this.DS.sentIsVerifiedData(false);
      if (attestAndSubmitRoute) {
        this.completenessCheckHandler();
      }
    });
  }

  isInstanceProcessedOrFailed() {
    // Use service method to get current value synchronously without creating subscription
    const currentRules = this.DS.getCurrentDisplayRules();
    const instanceStatus = currentRules?.displayedStatus;
    return instanceStatus === 'Filing Processed' || instanceStatus === 'Failed Processing';
  }

  saveData() {
    delete this.IS.currentInstance.collectedData?.cloudwell;
    this.onSave({ state: { beastNavEventKey: 'save' } });
  }

  /**
   * Override parent class method to handle individual changes toggle
   */
  onToggleRedline(checked: boolean) {
    this.redlineChecked = checked;

    if (checked) {
      // Determine which type of redline to show based on individual changes toggle
      const toggleType = this.individualChangesChecked ? 'individual' : 'all';
      this.redline.next({ value: true, toggleType });
    } else {
      this.redline.next(false);
    }

    this.updateCompletenessCheckTitle();
  }

  /**
   * Update the redline widget title based on current toggle states
   */
  private updateCompletenessCheckTitle() {
    const title = this.individualChangesChecked ? 'View Individual Changes' : 'View All Changes';
    console.log('Setting redline widget title to:', title);
  }

  @HostListener('document:keydown', ['$event'])
  onKeyDown(e: KeyboardEvent) {
    if (e.code === 'Enter') {
      if (e.target === document.getElementById('completeness-check-button')) {
        this.onValidate();
      } else if (e.target === document.getElementById('form-view-button')) {
        this.onFormView();
      } else if (!this.saveInProgress && e.target === document.getElementById('save-button')) {
        this.onSave({ state: { beastNavEventKey: 'save' } });
      } else if (e.target === document.getElementById('print-button')) {
        this.onPrint();
      } else if (e.target === document.getElementById('redline-slider')) {
        this.redlineChecked = !this.redlineChecked;
        this.onToggleRedline(this.redlineChecked);
      }
    }
  }

  onFormView() {
    if (this.viewType === 'formView') {
      return;
    }

    this.closeCC();
    this.updateLastOpenedSection();

    if (this.rampDoArrayDirtyCheck()) {
      this.OS.openStayLeaveModal()
        .afterClosed$.toPromise()
        .then((response) => {
          if (response.data) {
            this.openFormView('formViewIndividualInformation');
          }
        });
    } else {
      if (this.section !== 'attestAndSubmit' && this.section?.split(';')[0] !== 'guidance') {
        this.openFormView('formView' + this.section?.charAt(0)?.toUpperCase() + this.section?.slice(1));
      } else {
        const openView = this.section === 'attestAndSubmit' ? 'all' : 'formViewDocuments';
        this.openFormView(openView);
      }
    }
  }

  rampDoArrayDirtyCheck() {
    return doArrayDirtyCheck();
  }

  disablePreviousButton() {
    if (this.section === 'identifyingInformation') {
      return true;
    } else {
      return false;
    }
  }

  /**
   * Function : To change Next and Previous Button names based on section
   * @returns : associated section names for buttons
   */
  footerButtonTextSetter(section: string, position: string) {
    this.navSelectedSection = section;
    let buttonText = '';
    const sectionNavData = this.sectionNavData?.[0]?.children?.reduce((acc, item) => {
      if (!item.skip) {
        acc.push(item);
      }
      if (item.children?.length) {
        acc = [...acc, ...item.children.filter((i) => !i.skip)];
      }
      return acc;
    }, []);
    sectionNavData?.forEach((child: any, index: number) => {
      if (section == child.id && position == 'next') {
        buttonText = sectionNavData[index + 1]?.id.includes('guidance')
          ? `Agency Guidance - ${sectionNavData[index + 1]?.text?.split(':')[0]}`
          : sectionNavData[index + 1]?.text;
        return;
      } else if (section == child.id && position == 'previous') {
        buttonText = sectionNavData[index - 1]?.id.includes('guidance')
          ? `Agency Guidance - ${sectionNavData[index - 1]?.text?.split(':')[0]}`
          : sectionNavData[index - 1]?.text;
        return;
      }
    });
    return buttonText;
  }

  retrieveTemplateInfoFromCurrentInstance(): {
    templateVersion;
    templateName;
    templateNamespace;
    id;
    currentEnvVersion;
  } {
    const { templateVersion, templateName, templateNamespace, id } = this.IS.currentInstance;
    return {
      templateVersion,
      templateName,
      templateNamespace,
      id,
      currentEnvVersion: environment.versions[(templateVersion ?? environment.currentVersion) as Versions],
    };
  }

  /**
   * Triggers a request for attestation from company user
   */
  requestAttestation() {
    this.OS.openModal(`Confirm Request Attestation`, `info`, `Are you sure you want to request attestation?`, [
      { name: 'Cancel', value: false, theme: 'secondary', 'aria-label': 'Cancel attestation request' },
      { name: 'Yes', value: true, theme: 'primary', 'aria-label': 'Confirm attestation request' },
    ])
      .afterClosed$.toPromise()
      .then((response) => {
        if (response.data) {
          this.isRequestAttestationDisabledSub$.next(true);
          const { templateVersion, templateName, templateNamespace, id, currentEnvVersion } =
            this.retrieveTemplateInfoFromCurrentInstance();
          this.ES.partialUpdateInstanceUsingPATCH(templateNamespace, templateName, templateVersion, id, {
            instanceMetadata: {
              status: 'DRAFT',
              attributes: {
                attestationStatus: 'SENT',
              },
            },
          } as unknown as CollectionInstanceUpdateRequest).subscribe(
            (resp) => {
              console.info(resp, 'Attestation requested successfully. Redirecting to WM: pending record page');
              this.IS.handleSuccessResponse(this.successfulRequestAttestation);
              timer(5000)
                .pipe(takeUntil(this.destroySub$))
                .subscribe((r) => {
                  window.open(currentEnvVersion?.urls?.workManagement, '_self');
                });
            },
            (err) => {
              this.isRequestAttestationDisabledSub$.next(false);
              this.IS.handleErrorResponse(err);
            },
          );
        }
      });
  }

  /**
   * Attest form as Individual User
   */
  attestData() {
    this.OS.openModal(`Attestation Confirmation`, `info`, `Are you sure you want to attest?`, [
      { name: 'Cancel', value: false, theme: 'secondary', 'aria-label': 'Cancel attestation' },
      { name: 'Attest', value: true, theme: 'primary', 'aria-label': 'Confirm attestation' },
    ])
      .afterClosed$.toPromise()
      .then((response) => {
        if (response.data) {
          const { templateVersion, templateName, templateNamespace, id, currentEnvVersion } =
            this.retrieveTemplateInfoFromCurrentInstance();
          const signatureKey = 'indvlAcknowledgementConsent';

          this.ES.setInstanceSignatureUsingPUT(templateNamespace, templateName, templateVersion, id, signatureKey, {
            finraVerified: false,
          })
            .pipe(
              filter((res) => !!res),
              concatMap((res) =>
                this.ES.partialUpdateInstanceUsingPATCH(templateNamespace, templateName, templateVersion, id, {
                  instanceMetadata: { status: 'DRAFT' },
                } as unknown as CollectionInstanceUpdateRequest),
              ),
            )
            .subscribe(
              (res) => {
                if (res) {
                  console.info(res, 'Attestation verified successfully. Redirecting to IP: My NMLS');
                  this.IS.handleSuccessResponse(this.successfulAttestation);
                  timer(5000)
                    .pipe(takeUntil(this.destroySub$))
                    .subscribe((r) => {
                      window.open(currentEnvVersion?.urls.individualProfile + '?activeTab=home', '_self');
                    });
                }
              },
              (err) => {
                this.IS.handleErrorResponse(err);
              },
            );
        }
      });
  }

  openTermsModal() {
    // Payment/Invoice not implemented for MU3 — submit directly
    this.submit.next({});
  }

  ngOnDestroy(): void {
    this.destroySub$.next();
    this.destroySub$.complete();
  }
}
